'use client'

import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import { Brain, RotateCcw, ArrowLeft, ArrowRight } from 'lucide-react'

import QuestionCard from '@/components/freetool/QuestionCard'
import ProgressBar from '@/components/freetool/ProgressBar'
import dynamic from 'next/dynamic'

import type {
  Answer,
  Question,
  AssessmentResult,
  AQResult,
} from '@/types/assessment'
import { calculateResults as calcASRS } from '@/utils/scoringEngine'
import { calculateAQResults as calcAQ } from '@/utils/scoringEngine'
import { categoryInfo } from '@/data/asdQuestions'

// shadcn/ui
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const Results = dynamic(() => import('@/components/freetool/Results'), {
  ssr: false,
})
const UserDetailsModal = dynamic(
  () => import('@/components/freetool/UserDetailsModal'),
  { ssr: false },
)

type Kind = 'adhd' | 'asd'
type Option = { value: number; label: string; color: string }
type Theme = { from: string; to: string; dot: string }

export type AssessmentFlowProps = {
  kind: Kind
  title: string
  sublabel: string
  questions: Question[]
  options: Option[]
  gridClass: string
  badgeClass: string
  theme: Theme
  pdfLogoUrl?: string
  backHref?: string
}

const AssessmentFlow: React.FC<AssessmentFlowProps> = ({
  kind,
  title,
  sublabel,
  questions,
  options,
  gridClass,
  badgeClass,
  theme,
  pdfLogoUrl,
  backHref,
}) => {
  const [step, setStep] = useState<'intro' | 'assessment' | 'results'>('intro')
  const [answers, setAnswers] = useState<Answer[]>([])
  const [idx, setIdx] = useState(0)
  const [showUser, setShowUser] = useState(false)
  const [user, setUser] = useState<{
    name: string
    dob: string
    gender: string
    phoneNo: string
  } | null>(null)
  const [busy, setBusy] = useState(false)

  const q = questions[idx]
  const currentAnswer = answers.find((a) => a.questionId === q?.id)

  const chips = useMemo(() => {
    if (!q) return []
    if (kind === 'adhd') {
      return [
        {
          label: `Part ${q.part}`,
          className:
            q.part === 'A'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-purple-100 text-purple-800',
        },
        {
          label: q.category,
          className:
            q.category === 'inattention'
              ? 'bg-orange-100 text-orange-800'
              : 'bg-green-100 text-green-800',
        },
      ]
    }
    const cat = categoryInfo[q.category as keyof typeof categoryInfo]
    return [
      { label: cat.name, className: cat.color, icon: <span>{cat.icon}</span> },
      ...(q.reverse
        ? [{ label: 'Reverse Scored', className: 'bg-gray-100 text-gray-600' }]
        : []),
    ]
  }, [q, kind])

  const onAnswerChange = (questionId: number, value: number) =>
    setAnswers((prev) => {
      const i = prev.findIndex((a) => a.questionId === questionId)
      if (i > -1) {
        const clone = [...prev]
        clone[i] = { questionId, value }
        return clone
      }
      return [...prev, { questionId, value }]
    })

  const restart = () => {
    if (
      !confirm(
        'Restarting will erase all your answers.\n\nDo you want to start over?',
      )
    )
      return
    setStep('intro')
    setAnswers([])
    setIdx(0)
    setUser(null)
    setShowUser(false)
  }

  const handleUserSubmit = async (details: {
    name: string
    dob: string
    gender: string
    phoneNo: string
  }) => {
    setBusy(true)
    try {
      const res = await fetch('/api/assessments/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, user: details, answers }),
        cache: 'no-store',
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(
          err?.error ? JSON.stringify(err.error) : 'Failed to save assessment',
        )
      }
      setUser(details)
      setShowUser(false)
      setStep('results')
    } finally {
      setBusy(false)
    }
  }

  /** ---------- Intro ---------- */
  if (step === 'intro') {
    return (
      <section
        className={`min-h-[calc(100vh-80px)] bg-gradient-to-br ${kind === 'adhd' ? 'from-blue-50' : 'from-indigo-50'} via-white to-purple-50 flex items-center justify-center pt-20 sm:pt-24 pb-8 px-3 sm:px-4`}
      >
        <div className="w-full max-w-3xl mx-auto">
          <Card className="rounded-xl sm:rounded-2xl shadow-2xl">
            <CardContent className="p-5 sm:p-8">
              <div className="relative flex flex-col items-center gap-3 sm:gap-4">
                <div
                  className={`bg-gradient-to-r ${theme.from} ${theme.to} p-3 sm:p-4 rounded-full`}
                >
                  <Brain className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                </div>

                {backHref && (
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    className="absolute left-0 top-0"
                  >
                    <Link href={backHref} title="Back">
                      <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Link>
                  </Button>
                )}

                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center leading-snug">
                  {title}
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 text-center">
                  {sublabel}
                </p>

                <Button
                  onClick={() => setStep('assessment')}
                  className={`bg-gradient-to-r ${theme.from} ${theme.to} text-white px-6 py-5 sm:px-8 sm:py-6 mt-2 text-sm sm:text-base`}
                >
                  Start Assessment
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    )
  }

  /** ---------- Results ---------- */
  if (step === 'results' && user) {
    const results: AssessmentResult | AQResult =
      kind === 'adhd' ? calcASRS(answers) : calcAQ(answers)
    return (
      <section
        className={`min-h-screen bg-gradient-to-br ${kind === 'adhd' ? 'from-blue-50' : 'from-indigo-50'} via-white to-purple-50 pt-24 sm:pt-28 md:pt-32 pb-8`}
      >
        <Results
          kind={kind}
          results={results}
          userDetails={user}
          onRestart={restart}
          showCallback
          pdfLogoUrl={pdfLogoUrl}
        />
      </section>
    )
  }

  /** ---------- Assessment ---------- */
  return (
    <section
      className={`min-h-[calc(100vh-80px)] bg-gradient-to-br ${kind === 'adhd' ? 'from-blue-50' : 'from-indigo-50'} via-white to-purple-50 flex flex-col justify-center pt-20 sm:pt-24 pb-8`}
    >
      <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 flex flex-col gap-4 sm:gap-6">
        {!showUser && (
          <>
            {/* Header / Progress */}
            <Card className="rounded-xl sm:rounded-2xl shadow-lg">
              <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Brain
                      className={`w-5 h-5 sm:w-6 sm:h-6 shrink-0 ${kind === 'adhd' ? 'text-blue-600' : 'text-indigo-600'}`}
                    />
                    <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 truncate">
                      {title.replace(' Tool', '')}
                    </h1>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-4">
                    {backHref && (
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        title="Back"
                        className="w-8 h-8 sm:w-9 sm:h-9"
                      >
                        <Link href={backHref}>
                          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        </Link>
                      </Button>
                    )}
                    <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap tabular-nums">
                      {idx + 1} of {questions.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Restart"
                      onClick={restart}
                      className="w-8 h-8 sm:w-9 sm:h-9"
                    >
                      <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0 px-4 sm:px-6 pb-4">
                <div className="flex flex-col gap-1.5 sm:gap-2">
                  <ProgressBar
                    current={idx + 1}
                    total={questions.length}
                    indicatorClassName="[&>div]:bg-gradient-to-r [&>div]:from-indigo-500 [&>div]:to-purple-600"
                  />
                  <div className="text-center" aria-live="polite">
                    <span className="text-xs sm:text-sm text-gray-500">
                      {answers.length} of {questions.length} questions answered
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Question */}
            {q && (
              <div className="flex">
                <div className="flex-1 min-w-0">
                  <QuestionCard
                    question={q}
                    answer={currentAnswer}
                    onAnswerChange={onAnswerChange}
                    questionNumber={idx + 1}
                    options={options}
                    chips={chips}
                    gridClass={gridClass}
                    badgeClass={badgeClass}
                  />
                </div>
              </div>
            )}

            {/* Pager */}
            <div className="mt-1 sm:mt-2 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
              <Button
                variant="secondary"
                onClick={() => idx > 0 && setIdx(idx - 1)}
                disabled={idx === 0}
                className="w-full sm:w-auto text-sm"
              >
                Previous
              </Button>

              {/* Progress indicator - dots for ≤20 questions, compact bar for more */}
              <div className="hidden sm:flex items-center justify-center py-1">
                {questions.length <= 20 ? (
                  <div className="flex gap-1">
                    {questions.map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-2 rounded-full shrink-0 transition-transform duration-200 ${
                          i === idx
                            ? `${theme.dot} scale-150`
                            : answers.some(
                                  (a) => a.questionId === questions[i].id,
                                )
                              ? 'bg-green-500'
                              : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-32 md:w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${theme.from} ${theme.to} transition-all duration-300 ease-out`}
                        style={{
                          width: `${((idx + 1) / questions.length) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                      {idx + 1} / {questions.length}
                    </span>
                  </div>
                )}
              </div>

              <Button
                onClick={() =>
                  idx < questions.length - 1
                    ? setIdx(idx + 1)
                    : setShowUser(true)
                }
                disabled={currentAnswer === undefined}
                className={`w-full sm:w-auto text-white text-sm bg-gradient-to-r ${theme.from} ${theme.to} hover:opacity-90`}
              >
                {idx === questions.length - 1 ? 'Submit Assessment' : 'Next'}
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Details modal - non-dismissible */}
      {showUser && (
        <UserDetailsModal
          isOpen
          onClose={() => setShowUser(false)}
          onSubmit={handleUserSubmit}
          dismissible={false}
        />
      )}

      {/* Busy overlay */}
      {busy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <Card className="p-4 sm:p-6 mx-4">
            <div className="text-center">
              <div
                className={`animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 ${kind === 'adhd' ? 'border-blue-600' : 'border-indigo-600'} mx-auto mb-3 sm:mb-4`}
              />
              <p className="text-sm sm:text-base text-gray-600">
                Saving your assessment...
              </p>
            </div>
          </Card>
        </div>
      )}
    </section>
  )
}

export default AssessmentFlow
